import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Users, Plus } from "lucide-react";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { Panel, EmptyState } from "@/components/admin/ui";
import { formatCents, fmtDate } from "./billingMeta";
import { AddCustomerModal } from "./AddCustomerModal";
import { CustomerDetailDrawer } from "./CustomerDetailDrawer";
import { InvoiceDetailDrawer } from "./InvoiceDetailDrawer";

const btn = "inline-flex items-center gap-1.5 rounded-xl border border-transparent bg-[#0052FF] px-3.5 py-2 text-xs font-bold text-white transition-colors duration-150 hover:bg-[#0040CC]";

export const BillingCustomersTab = ({ API, canManage }) => {
  const [customers, setCustomers] = useState(null);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [openCustomerId, setOpenCustomerId] = useState(null);
  const [openInvoiceId, setOpenInvoiceId] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/org/billing/customers`, { params: { search: search || undefined }, withCredentials: true })
      .then(({ data }) => setCustomers(data.customers))
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
  }, [API, search]);

  useEffect(() => { load(); }, [load]);

  if (error) return <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="billing-customers-error">{error}</p>;

  return (
    <div data-testid="billing-customers-tab">
      <Panel
        title="Customers"
        subtitle="A financial view of your existing customers — identity is shared across Nova Opsis."
        action={canManage && <button type="button" onClick={() => setShowAdd(true)} data-testid="billing-customers-add-btn" className={btn}><Plus size={14} /> Add Customer</button>}
      >
        <div className="mb-4"><input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search customers…" data-testid="billing-customers-search-input" className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs dark:border-white/15 dark:bg-[#0B1220] dark:text-white" /></div>

        {!customers ? (
          <p className="text-sm text-slate-400" data-testid="billing-customers-loading">Loading…</p>
        ) : customers.length === 0 ? (
          <EmptyState icon={Users} title="No customers yet." message="Customers from Sales Leads, or added here directly, will show up with their billing summary." testId="billing-customers-empty" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="billing-customers-table">
              <thead><tr className="text-left text-xs font-bold uppercase tracking-wide text-slate-400"><th className="pb-2">Customer</th><th className="pb-2">Outstanding</th><th className="pb-2">Open Invoices</th><th className="pb-2">Last Invoice</th><th className="pb-2">Last Payment</th></tr></thead>
              <tbody>
                {customers.map((c) => (
                  <tr key={c.customer_id} className="cursor-pointer border-t border-slate-100 hover:bg-slate-50 dark:border-white/5 dark:hover:bg-white/5" onClick={() => setOpenCustomerId(c.customer_id)} data-testid={`billing-customer-row-${c.customer_id}`}>
                    <td className="py-2.5 font-bold text-[#0052FF]">{c.first_name} {c.last_name}</td>
                    <td className="py-2.5 font-semibold">{formatCents(c.outstanding_cents)}</td>
                    <td className="py-2.5">{c.open_invoice_count}</td>
                    <td className="py-2.5 text-slate-500 dark:text-slate-400">{fmtDate(c.last_invoice_at)}</td>
                    <td className="py-2.5 text-slate-500 dark:text-slate-400">{fmtDate(c.last_payment_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {showAdd && <AddCustomerModal API={API} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {openCustomerId && !openInvoiceId && <CustomerDetailDrawer API={API} customerId={openCustomerId} onClose={() => setOpenCustomerId(null)} onOpenInvoice={setOpenInvoiceId} />}
      {openInvoiceId && <InvoiceDetailDrawer API={API} invoiceId={openInvoiceId} canManage={canManage} onClose={() => setOpenInvoiceId(null)} onChanged={load} onEdit={() => {}} />}
    </div>
  );
};
